import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AchatEntity } from '../../../entities/achat.entity';
import {
  TitreBidAskType,
  TitreEntity,
  TitreOptionsType,
} from '../../../entities/titre.entity';
import { Layout } from '../../shared/Layout';
import { Preloader } from '../../shared/Preloader';
import { Activity } from '../components/Activity';
import { GraphData } from '../components/GraphData';
import { InstrumentDetail } from '../components/IntrusmentDetail';
import { Monitor } from '../components/Monitor';
import { News } from '../components/News';
import { OrderEntry } from '../components/OrderEntry';
import {
  getAvailableTitres,
  getTitreBidAsk,
  getTitreMarket,
  getTitreOptions,
  getUserOrders,
  getUserTransactions,
} from '../network';

export type OrderPrefill = {
  prix: number;
  qte: number;
};

const uniq = <T,>(values: T[]) =>
  Array.from(
    new Set(
      values.filter((value) => value !== undefined && value !== null && value !== '') as T[],
    ),
  );

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? value : []);

const buildTitreOptionsFromTitres = (
  titres: TitreEntity[],
): TitreOptionsType => ({
  pays: uniq(titres.map((titre) => titre.pays)),
  region: uniq(titres.map((titre) => titre.region)),
  marche: uniq(titres.map((titre) => titre.marche)),
  instrument: uniq(titres.map((titre) => titre.instrument)),
  dateMaturite: uniq(titres.map((titre) => titre.dateMaturite)),
  dateEmission: uniq(titres.map((titre) => titre.dateEmission)),
  coupon: uniq(titres.map((titre) => Number(titre.tauxMarginal)).filter(Number.isFinite)),
});

const Container = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(420px, 0.95fr);
  gap: 8px;
  height: calc(100vh - 132px);
  min-height: 0;
  overflow: hidden;
  padding: 6px 8px 8px;
  background:
    linear-gradient(180deg, rgba(4, 16, 24, 0.1), transparent 180px),
    var(--mt-bg);
  color: var(--mt-text);

  .dashboard-card {
    min-width: 0;
    overflow: hidden;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
    backdrop-filter: blur(14px);
  }

  .dashboard-card > div {
    height: 100%;
  }

  .dashboard-card > div > .top {
    min-height: 38px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  > .box1 {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    gap: 8px;

    > .container11 {
      flex: 0 0 30%;
      min-height: 280px;
    }

    > .container12 {
      display: grid;
      grid-template-columns: minmax(250px, 0.85fr) minmax(0, 1.15fr);
      gap: 8px;
      flex: 0 0 27%;
      min-height: 230px;

      > .container121 {
        min-width: 0;
      }

      > .container122 {
        height: 100%;
        min-width: 0;
      }
    }

    > .container13 {
      flex: 1 1 0;
      min-height: 190px;
    }
  }

  > .box2 {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    gap: 8px;

    > .container21 {
      flex: 0 0 66%;
      min-height: 0;
    }

    > .container22 {
      flex: 1 1 0;
      min-height: 220px;
    }
  }

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;

    > .box2 {
      order: -1;
    }
  }

  @media (max-width: 760px) {
    padding: 6px;

    > .box1 > .container12 {
      grid-template-columns: 1fr;
    }

    > .box2 > .container21,
    > .box1 > .container11,
    > .box1 > .container12,
    > .box1 > .container13 {
      min-height: auto;
    }
  }
`;

export const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTitre, setSelectedTitre] = useState<TitreEntity>();
  const [selectedTitreforOPE, setSelectedTitreForOPE] = useState<TitreEntity>();
  const [selectedOperation, setSelectedOperation] = useState('0');
  const [orderPrefill, setOrderPrefill] = useState<OrderPrefill>();
  const connectedUser = useSelector((state: any) => state.userReducer).user;
  const [titres, setTitres] = useState<TitreEntity[]>([]);
  const [titreOptions, setTitreOptions] = useState<TitreOptionsType>();
  const [orders, setOrders] = useState<AchatEntity[]>([]);
  const [transactions, setTransactions] = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [titreBidAsk, setTitreBidAsk] = useState<TitreBidAskType>({
    bid: 0,
    ask: 0,
    vendeurMKT: 0,
    acheteurMKT: 0,
  });
  const [detVendeur, setDetVendeur] = useState(''); // alimenté lorsqu'on clique pour la vente d'un titre dans "Portefeuille"

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTitre) {
      getTitreMarket(selectedTitre.id).then(({ data }) => {
        setMarketData(data);
      });
      getTitreBidAsk(selectedTitre.id).then(({ data }) => {
        setTitreBidAsk(data);
      });
    }
  }, [selectedTitre]);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const titresResponse = await getAvailableTitres(
        connectedUser.id,
      );
      const titresData = asArray<TitreEntity>(titresResponse.data);
      setTitres(titresData);

      const titreOptionsResponse = await getTitreOptions();
      if (
        titreOptionsResponse.statusCode?.toString()[0] === '2' &&
        titreOptionsResponse.data &&
        typeof titreOptionsResponse.data === 'object' &&
        !Array.isArray(titreOptionsResponse.data)
      ) {
        setTitreOptions(titreOptionsResponse.data);
      } else {
        setTitreOptions(buildTitreOptionsFromTitres(titresData));
      }

      const [ordersResponse, transactionsResponse] =
        await Promise.all([
          getUserOrders(connectedUser.id),
          getUserTransactions(connectedUser.id),
        ]);

      setOrders(asArray<AchatEntity>(ordersResponse.data));
      setTransactions(asArray(transactionsResponse.data));
    } finally {
      setIsLoading(false);
    }
  };

  const getTitreForOperation = (
    b: TitreEntity,
    ope: '1' | '2',
    detUrl?: string,
    prefill?: OrderPrefill,
  ) => {
    setSelectedTitre(b);
    setSelectedOperation(ope);
    setOrderPrefill(prefill);
    if (detUrl) setDetVendeur(detUrl);
  };

  // update profondeur table in child component if match occurs
  const updateProfondeur = () => {
    if (selectedTitre) {
      getTitreMarket(selectedTitre.id).then(({ data }) => {
        setMarketData(data);
      });
      getTitreBidAsk(selectedTitre.id).then(({ data }) => {
        setTitreBidAsk(data);
      });
      getUserOrders(connectedUser.id).then(({ data }) => {
        setOrders(data);
      });
      getUserTransactions(connectedUser.id).then(({ data }) => {
        setTransactions(data);
      });
    }
  };

  // update order table in child component if modify or delete occurs
  const updateOders = () => {
    if (selectedTitreforOPE) {
      getTitreMarket(selectedTitreforOPE.id).then(({ data }) => {
        setMarketData(data);
      });
      getTitreBidAsk(selectedTitreforOPE.id).then(({ data }) => {
        setTitreBidAsk(data);
      });
      getUserOrders(connectedUser.id).then(({ data }) => {
        setOrders(data);
      });
    }
  };

  if (isLoading) return <Preloader />;

  return (
    <Layout>
      <Container>
        <div className='box1'>
          <div className='container11 dashboard-card'>
            <OrderEntry
              selectedTitre={selectedTitre}
              selectedOpe={selectedOperation}
              orderPrefill={orderPrefill}
              detenteurVendeur={detVendeur} // alimenté uniquement pour les opérations de vente
              titreBidAsk={titreBidAsk}
              updateProfondeur={updateProfondeur}
            />
          </div>
          <div className='container12'>
            <div className='container121 dashboard-card'>
              <InstrumentDetail
                selectedTitre={selectedTitre}
                titreBidAsk={titreBidAsk}
              />
            </div>
            <div className='container122 dashboard-card'>
              <GraphData marketData={marketData} />
            </div>
          </div>
          <div className='container13 dashboard-card'>
            <Activity
              orders={orders}
              transactions={transactions}
              updateOders={updateOders}
              getSelectedTitre={setSelectedTitreForOPE}
            />
          </div>
        </div>

        <div className='box2'>
          <div className='container21 dashboard-card'>
            <Monitor
              titres={titres}
              titreOptions={titreOptions}
              getTitre={getTitreForOperation}
            />
          </div>
          <div className='container22 dashboard-card'>
            <News />
          </div>
        </div>
      </Container>
    </Layout>
  );
};
